import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRequestData, useLabels, usePrefs } from 'wsm-common-data';
import { Modal } from 'react-bootstrap';
import Parse from 'html-react-parser';
import { getDiscountDisplay } from '../utilities/general';
import { g, perpetualOptions } from '../global-constants';

const DetailsModal = ({
	isOpen,
	isMobile,
	onModalClose,
	promo,
	cta,
	applePassBtn
}) => {
	const labels = useLabels();
	const { locale } = useRequestData();
	const { reduceDollarSignSize } = usePrefs();
	const {
		additionalDetails = '',
		couponCode = '',
		description = '',
		disclaimer = '',
		discountType,
		discountValue,
		endDateDisplay = '',
		startDateDisplay = '',
		title = '',
		type
	} = promo;
	let discount = null;

	if ([g.PARTS, g.SERVICE].includes(type) && discountType !== 'NO_DISCOUNT') {
		discount = getDiscountDisplay(
			locale,
			discountType,
			discountValue,
			labels,
			reduceDollarSignSize
		);
	}

	useEffect(() => {
		// Refreshing our ui-dialog module
		if (typeof window !== 'undefined' && isMobile) {
			window.DDC.mobile.slidein.init();
		}
	}, [isMobile]);

	const validDates = () => {
		let output = null;

		switch (endDateDisplay) {
			case perpetualOptions.NO_SHOW:
				if (startDateDisplay === perpetualOptions.NO_SHOW_ON_SPECIAL) {
					output = <p>{labels.get('NO_EXPIRATION_DATE')}</p>;
				} else {
					output = (
						<p>
							{labels
								.get('START_DATE_END_DATE_NO_EXPIRATION')
								.replace('START_DATE', startDateDisplay)}
						</p>
					);
				}
				break;
			case perpetualOptions.NO_SHOW_ON_SPECIAL:
				if (startDateDisplay === perpetualOptions.NO_SHOW_ON_SPECIAL) {
					output = <p />;
				} else {
					output = (
						<p>
							{labels.get('OFFER_VALID_ON')} {startDateDisplay}
						</p>
					);
				}
				break;
			default:
				if (startDateDisplay === perpetualOptions.NO_SHOW_ON_SPECIAL) {
					output = (
						<p>
							{labels.get('EXPIRES_WS_SPECIALS')} {endDateDisplay}
						</p>
					);
				} else {
					output = (
						<p>
							{labels
								.get('OFFER_ONLY_VALID_THROUGH_DATES')
								.replace('START_DATE', startDateDisplay)
								.replace('END_DATE', endDateDisplay)}
						</p>
					);
				}
		}

		return output;
	};

	const ctaClick = () => {
		if (promo?.ctaConfig?.type === 'LEAD') {
			onModalClose('CTA');
		}
	};

	const parseText = (str) => {
		return Parse(`${str.replace(/\n/g, '<br>')}`);
	};

	return (
		<Modal
			show={isOpen}
			onHide={onModalClose}
			className={`m-1 ${isMobile ? 'dock-right' : ''}`}
		>
			<Modal.Header>
				<Modal.Title className="text-center">
					<button
						type="button"
						aria-label="Close"
						className="close text-muted"
						onClick={onModalClose}
					>
						<i
							className="ddc-icon ddc-icon-close"
							aria-hidden="true"
						/>
					</button>
					<span className="h3 my-0 px-5">
						{title} {discount}
					</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{validDates()}
				<p>{description}</p>
				{cta !== null && (
					<div
						onClick={ctaClick}
						onKeyDown={ctaClick}
						role="presentation"
					>
						{cta}
					</div>
				)}
				{applePassBtn !== null && <>{applePassBtn}</>}
				{additionalDetails ? (
					<>
						<h4 className="mt-2 mb-0 h5">
							{labels.get('DETAILS')}
						</h4>
						<p>{parseText(`${additionalDetails}`)}</p>
					</>
				) : null}
				{couponCode ? (
					<>
						<h4 className="mt-2 mb-0 h5">
							{labels.get('COUPON_CODE')}
						</h4>
						<p>{couponCode}</p>
					</>
				) : null}
				{disclaimer ? (
					<>
						<h4 className="mt-2 mb-0 h5">
							{labels.get('DISCLAIMER')}
						</h4>
						<div className="text-muted small">
							<p>{parseText(`${disclaimer}`)}</p>
						</div>
					</>
				) : null}
			</Modal.Body>
		</Modal>
	);
};

DetailsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	isMobile: PropTypes.bool.isRequired,
	onModalClose: PropTypes.func.isRequired,
	promo: PropTypes.shape({
		additionalDetails: PropTypes.string,
		couponCode: PropTypes.string,
		description: PropTypes.string,
		disclaimer: PropTypes.string,
		discountType: PropTypes.string,
		discountValue: PropTypes.number,
		endDateDisplay: PropTypes.string,
		startDateDisplay: PropTypes.string,
		title: PropTypes.string,
		type: PropTypes.string.isRequired,
		ctaConfig: PropTypes.shape({
			type: PropTypes.string
		})
	}),
	cta: PropTypes.shape({
		clickEvent: PropTypes.func,
		classes: PropTypes.arrayOf(PropTypes.string),
		isVisible: PropTypes.bool,
		label: PropTypes.string.isRequired,
		url: PropTypes.string.isRequired,
		promo: PropTypes.shape({}).isRequired
	}),
	applePassBtn: PropTypes.shape({
		title: PropTypes.string.isRequired,
		description: PropTypes.string,
		disclaimer: PropTypes.string,
		discountValue: PropTypes.string,
		endDateDisplay: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		promoDetails: PropTypes.string
	})
};

export default DetailsModal;
